import { exportToSvg } from "@excalidraw/utils";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

const THUMBNAIL_MAX_DIMENSION = 300;

/**
 * Generate a thumbnail for the given elements
 * Returns a base64-encoded SVG data URL
 */
export async function generateThumbnail(
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): Promise<string> {
  try {
    // Skip if no elements
    if (elements.length === 0) {
      return getEmptyThumbnailSvg();
    }

    // Export elements to SVG
    const svg = await exportToSvg({
      elements,
      appState: {
        ...appState,
        exportBackground: true,
        viewBackgroundColor: appState.viewBackgroundColor || "#ffffff",
      },
      files,
      exportPadding: 10,
      maxWidthOrHeight: THUMBNAIL_MAX_DIMENSION,
    });

    // Serialize SVG to string
    const svgString = svg.outerHTML;

    // Convert to base64 data URL
    const base64 = btoa(unescape(encodeURIComponent(svgString)));
    return `data:image/svg+xml;base64,${base64}`;
  } catch (error) {
    console.error("Error generating thumbnail:", error);
    return getEmptyThumbnailSvg();
  }
}

/**
 * Get an empty/placeholder thumbnail SVG
 */
function getEmptyThumbnailSvg(): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="225" viewBox="0 0 300 225">
    <rect width="300" height="225" fill="#f5f5f5"/>
    <text x="150" y="112.5" text-anchor="middle" dominant-baseline="middle" fill="#999" font-size="14" font-family="system-ui, sans-serif">
      Empty Drawing
    </text>
  </svg>`;

  const base64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${base64}`;
}
